// @flow strict

// $FlowFixMe[nonstrict-import]
import typeof {ApiError} from 'src/utils/errors';

import {createSelector} from 'reselect';
import keyBy from 'lodash/keyBy';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import uniqueId from 'lodash/uniqueId';
import intersection from 'lodash/intersection';

// $FlowFixMe[nonstrict-import]
import * as dateFns from 'date-fns';


const {addDays, getTime, subDays} = dateFns;
// $FlowFixMe[nonstrict-import]
import produce from 'immer'; // eslint-disable-line import/no-unresolved

import {values} from 'src/utils/object';
import type {
  AgentSettings,
  BookingAccount,
  BookingSlot,
  ConnectionState,
  DayOfWeek,
  SchedulerCalendars,
  SchedulerTemplate,
  SchedulerAccount,
  SchedulerAccounts,
  SchedulerTemplates,
  PagedScheduledEvents,
  ScheduledEventsApiParams,
  ScheduledEvent,
  Duration,
  Reminder,
} from 'src/types/scheduler';

// We use this type BookingRow because the server has a different
// internal structure (BookingSlot) that has a single start/end combo
// _for each day_. To match the ui and simplify edits, we remap this to
// a BookingRow
export type BookingRow = {
  day_of_week?: DayOfWeek,
  days: DayOfWeek[],
  start_time: string,
  end_time: string,
  rule_id: string,
};

export type EditorActionTypes =
  | {
      type: 'init',
      payload: {
        templates: SchedulerTemplates,
        currentAgentId?: string,
        isAdmin?: boolean,
        isHostManager: boolean,
        hasImpersonationPermission?: boolean,
      },
    }
  | {
      type: 'update',
      payload: {templateId: string, newData: $Shape<SchedulerTemplate>},
    }
  | {
      type: 'update_and_save',
      payload: {templateId: string, newData: $Shape<SchedulerTemplate>},
    }
  | {
      type: 'set_should_save',
      payload: boolean,
    }
  | {type: 'set_merge_scheduled_events', payload: boolean}
  | {
      type: 'receive_scheduled_events',
      payload: PagedScheduledEvents,
    }
  | {type: 'receive_past_events', payload: PagedScheduledEvents}
  | {
      type: 'update_scheduled_events_api_params',
      payload: ScheduledEventsApiParams,
    }
  | {type: 'update_past_events_api_params', payload: ScheduledEventsApiParams}
  | {
      type: 'receive_accounts',
      payload: SchedulerAccounts,
    }
  | {type: 'receive_me', payload: SchedulerAccounts}
  | {type: 'receive_calendars', payload: SchedulerCalendars}
  | {
      type: 'receive_templates',
      payload: SchedulerTemplates,
    }
  | {type: 'set_actor_id', payload: ?string}
  | {type: 'delete_event', payload: string}
  | {
      type: 'update_events',
      payload: {[key: string]: {key: string, value: boolean}},
    }
  | {type: 'remove_template', payload: {id: string}}
  | {
      type: 'set_loading',
      payload: $Shape<{
        templates: boolean,
        accounts: boolean,
        teamTemplates: boolean,
        scheduledEvents: boolean,
        pastEvents: boolean,
        me: boolean,
      }>,
    }
  | {
      type: 'trigger_action',
      payload: $Shape<Triggers>,
    }
  | {
      type: 'update_template_booking_accounts',
      payload: {template_id: string, booking_account_ids: string[]},
    }
  | {
      type: 'update_template_booking_accounts_field',
      payload: {
        template_id: string,
        account_id: string,
        data: $Shape<BookingAccount>,
      },
    }
  | {
      type: 'set_role_for_account',
      payload: {
        template_id: string,
        account: BookingAccount,
        role: 'optional' | 'required' | 'host',
      },
    }
  | {
      type: 'booking_slot_type_change',
      payload: {
        account_id: string,
        template_id: string,
        booking_slot_type: string,
      },
    }
  | {
      type: 'set_initial_load_upcoming',
      payload: boolean,
    }
  | {
      type: 'set_initial_load_past',
      payload: boolean,
    }
  | {
      type: 'add_new_event',
      payload: ScheduledEvent,
    };

type Triggers = {
  fetchAccounts: boolean,
  fetchTemplates: boolean,
  fetchTeamTemplates: boolean,
  fetchPrimaryActor: boolean,
  fetchMe: boolean,
  save: boolean,
};

// TODO(marcos) should we add a Dispatch type? it's not more complex than this...
export type SchedulerDispatch = (EditorActionTypes) => void;

export type EditorState = {
  templates: {[id: string]: SchedulerTemplate},
  accounts: SchedulerAccounts,
  me: SchedulerAccounts,
  errors: {[route: string]: ?ApiError},
  calendars: SchedulerCalendars,
  currentAgentId: ?string,
  currentActorId: ?string,
  isAdmin: boolean,
  isHostManager: boolean,
  hasImpersonationPermission: boolean,
  loading: {
    accounts: boolean,
    teamTemplates: boolean,
    templates: boolean,
    scheduledEvents: boolean,
    pastEvents: boolean,
    me: boolean,
  },
  triggers: Triggers,
  scheduled_events: PagedScheduledEvents,
  past_events: PagedScheduledEvents,
  scheduled_events_api_params: ScheduledEventsApiParams,
  past_events_api_params: ScheduledEventsApiParams,
  merge_scheduled_events: boolean,
  initial_load_upcoming: boolean,
  initial_load_past: boolean,
};

export const templateEditorReducer = (
  state: EditorState,
  action: EditorActionTypes,
): EditorState => {
  switch (action.type) {
    case 'init':
      return initTemplates(action.payload);
    case 'trigger_action':
      return {...state, triggers: {...state.triggers, ...action.payload}};
    case 'update':
      const newTemplate = {
        ...state.templates[action.payload.templateId],
        ...action.payload.newData,
      };
      return {
        ...state,
        templates: {
          ...state.templates,
          [action.payload.templateId]: newTemplate,
        },
      };
    case 'update_and_save':
      const nextTemplate = {
        ...state.templates[action.payload.templateId],
        ...action.payload.newData,
      };
      return {
        ...state,
        templates: {[action.payload.templateId]: nextTemplate},
        triggers: {...state.triggers, save: true},
      };
    case 'set_merge_scheduled_events':
      return {...state, merge_scheduled_events: action.payload};
    case 'receive_scheduled_events':
      return {...state, scheduled_events: action.payload};
    case 'receive_past_events':
      return {...state, past_events: action.payload};
    case 'update_scheduled_events_api_params':
      return {...state, scheduled_events_api_params: action.payload};
    case 'update_past_events_api_params':
      return {...state, past_events_api_params: action.payload};
    case 'receive_accounts':
      return {
        ...state,
        accounts: action.payload,
        errors: {...state.errors, 'scheduler/accounts': undefined},
      };
    case 'receive_calendars':
      return {
        ...state,
        calendars: action.payload,
      };
    case 'receive_me':
      return {
        ...state,
        me: action.payload,
      };
    case 'receive_templates':
      return {
        ...state,
        templates: {...state.templates, ...keyBy(action.payload, 'id')},
      };
    case 'remove_template':
      const {[action.payload.id]: _removed, ...rest} = state.templates;
      return {
        ...state,
        templates: rest,
      };
    case 'set_loading':
      return {
        ...state,
        loading: {...state.loading, ...action.payload},
      };
    case 'delete_event':
      return {
        ...state,
        scheduled_events: {
          ...state.scheduled_events,
          data: state.scheduled_events.data.filter(
            (event) => event.id !== action.payload,
          ),
        },
      };
    case 'update_events':
      return {
        ...state,
        scheduled_events: {
          ...state.scheduled_events,

          data: state.scheduled_events.data.map((event) => {
            const eventsObject = action.payload || {};
            if (action.payload[event.id]) {
              const {key, value} = eventsObject[event.id];
              return {...event, [key]: value};
            }
            return event;
          }),
        },
      };
    case 'set_actor_id':
      return {...state, currentActorId: action.payload};
    case 'update_template_booking_accounts': {
      const defaultBookingRow: BookingRow = {
        days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        start_time: '09:00',
        end_time: '17:00',
        rule_id: uniqueId(),
      };
      const {template_id, booking_account_ids} = action.payload;
      const next = produce(state, (draft) => {
        const currentAccts = keyBy(
          draft.templates[template_id].booking_accounts,
          'account_id',
        );
        draft.templates[template_id].booking_accounts = booking_account_ids.map(
          (id) => {
            // skip over existing booking accounts
            if (currentAccts[id] != null) {
              return currentAccts[id];
              // prep new booking accounts by setting defaults
            } else {
              const calendars = getEditableCalendarsForAccount(
                state.calendars,
                id,
              ).filter((cal) => cal.is_primary);
              const settings = selectAgentSettingsForAccountId(state, id);

              if (
                draft.templates[template_id].distribution_rule ===
                'panel_interview'
              ) {
                return {
                  account_id: id,
                  // $FlowIssue[unnecessary-optional-chain] calendars[0] _can_ be null
                  calendar_id: calendars[0]?.id,
                  is_required: true,
                  is_host: false,
                  open_booking_dates: [],
                  booking_slot_type: 'week_based_slot',
                  open_booking_slots: settings?.business_hours.length
                    ? settings.business_hours // i.e. if _any_ hours have been set, default is []
                    : bookingRowsAsBookingSlots([defaultBookingRow]),
                  timezone: settings?.timezone ?? 'America/Los_Angeles',
                };
              } else {
                return {
                  account_id: id,
                  // $FlowIssue[unnecessary-optional-chain] calendars[0] _can_ be null
                  calendar_id: calendars[0]?.id,
                  open_booking_slots: settings?.business_hours.length
                    ? settings.business_hours // i.e. if _any_ hours have been set, default is []
                    : bookingRowsAsBookingSlots([defaultBookingRow]),
                  timezone: settings?.timezone ?? 'America/Los_Angeles',
                };
              }
            }
          },
        );
      });
      return next;
    }
    case 'update_template_booking_accounts_field': {
      const next = produce(state, (draft) => {
        const {template_id, account_id, data} = action.payload;
        const accts = draft.templates[template_id].booking_accounts;
        const acctIdx = accts.findIndex(
          (acct) => acct.account_id === account_id,
        );
        if (acctIdx !== -1) {
          const bookingAccount =
            draft.templates[template_id].booking_accounts[acctIdx];
          draft.templates[template_id].booking_accounts[acctIdx] = {
            ...bookingAccount,
            ...data,
          };
        }
      });
      return next;
    }
    case 'set_role_for_account': {
      const {account, role, template_id} = action.payload;
      const next = produce(state, (draft) => {
        draft.templates[template_id].booking_accounts.forEach((ba) => {
          if (role === 'host') {
            if (ba.account_id === account.account_id) {
              ba.is_host = true;
              ba.is_required = true;
            } else {
              ba.is_host = false;
            }
          } else {
            if (ba.account_id === account.account_id) {
              if (role === 'optional') {
                ba.is_required = false;
              } else if (role === 'required') {
                ba.is_required = true;
              }
            }
          }
        });
      });
      return next;
    }
    case 'booking_slot_type_change': {
      const {template_id, booking_slot_type, account_id} = action.payload;

      const next = produce(state, (draft) => {
        const index = draft.templates[template_id].booking_accounts.findIndex(
          (acct) => acct.account_id === account_id,
        );

        const template = draft.templates[template_id].booking_accounts[index];
        template.booking_slot_type = booking_slot_type;
      });
      return next;
    }
    case 'set_initial_load_upcoming':
      return {...state, initial_load_upcoming: action.payload};

    case 'set_initial_load_past':
      return {...state, initial_load_past: action.payload};
    case 'add_new_event': {
      const updatedEvents = [...state.scheduled_events.data, action.payload];
      updatedEvents.sort(
        (a, b) => new Date(a.event_start) - new Date(b.event_start),
      );
      return {
        ...state,
        scheduled_events: {
          total_pages: state.scheduled_events.total_pages,
          data: updatedEvents,
        },
      };
    }
  }

  return state;
};

export const updateTemplateField = (
  templateId: string,
  fieldName: string,
  fieldValue: mixed,
  triggerSave?: boolean = false,
):
  | {
      type: 'update',
      payload: {templateId: string, newData: $Shape<SchedulerTemplate>},
    }
  | {
      type: 'update_and_save',
      payload: {templateId: string, newData: $Shape<SchedulerTemplate>},
    } =>
  triggerSave
    ? {
        type: 'update_and_save',
        payload: {templateId, newData: {[fieldName]: fieldValue}},
      }
    : {
        type: 'update',
        payload: {templateId, newData: {[fieldName]: fieldValue}},
      };

export const setShouldSaveTemplate = (
  shouldSave: boolean,
): {type: 'trigger_action', payload: $Shape<Triggers>} => ({
  type: 'trigger_action',
  payload: {save: shouldSave},
});
export const setShouldRefetchAccounts = (
  shouldRefetch: boolean,
): {type: 'trigger_action', payload: $Shape<Triggers>} => ({
  type: 'trigger_action',
  payload: {fetchAccounts: shouldRefetch},
});
export const setShouldRefetchTemplates = (
  shouldRefetch: boolean,
): {type: 'trigger_action', payload: $Shape<Triggers>} => ({
  type: 'trigger_action',
  payload: {fetchTemplates: shouldRefetch},
});

export const setShouldRefetchTeamTemplates = (
  shouldRefetch: boolean,
): {type: 'trigger_action', payload: $Shape<Triggers>} => ({
  type: 'trigger_action',
  payload: {fetchTeamTemplates: shouldRefetch},
});

export const setShouldRefetchMe = (
  shouldRefetch: boolean,
): {type: 'trigger_action', payload: $Shape<Triggers>} => ({
  type: 'trigger_action',
  payload: {fetchMe: shouldRefetch},
});

export const receiveTemplates = (
  templates: SchedulerTemplates,
): {type: 'receive_templates', payload: SchedulerTemplates} => ({
  type: 'receive_templates',
  payload: templates,
});

export const addNewEvent = (
  event: ScheduledEvent,
): {type: 'add_new_event', payload: ScheduledEvent} => ({
  type: 'add_new_event',
  payload: event,
});

export const updateBookingAccountMembers = (
  templateId: string,
  bookingIds: string[],
): {
  type: 'update_template_booking_accounts',
  payload: {template_id: string, booking_account_ids: string[]},
} => ({
  type: 'update_template_booking_accounts',
  payload: {
    template_id: templateId,
    booking_account_ids: bookingIds,
  },
});

export const updateBookingSlotType = (
  accountId: string,
  templateId: string,
  string: string,
): {
  type: 'booking_slot_type_change',
  payload: {
    account_id: string,
    template_id: string,
    booking_slot_type: string,
  },
} => ({
  type: 'booking_slot_type_change',
  payload: {
    account_id: accountId,
    template_id: templateId,
    booking_slot_type: string,
  },
});

export function updateTemplateBookingAccountsField(
  template_id: string,
  data: $Shape<BookingAccount>,
  account_id: string,
): {
  type: 'update_template_booking_accounts_field',
  payload: {
    template_id: string,
    account_id: string,
    data: $Shape<BookingAccount>,
  },
} {
  return {
    type: 'update_template_booking_accounts_field',
    payload: {
      template_id,
      account_id,
      data,
    },
  };
}

export function setRoleForAccount(
  template_id: string,
  role: 'optional' | 'host' | 'required',
  account: BookingAccount,
): {
  type: 'set_role_for_account',
  payload: {
    template_id: string,
    role: 'optional' | 'host' | 'required',
    account: BookingAccount,
  },
} {
  return {
    type: 'set_role_for_account',
    payload: {
      template_id,
      account,
      role,
    },
  };
}

export const initTemplates = ({
  templates,
  currentAgentId,
  isAdmin = false,
  isHostManager = false,
  hasImpersonationPermission = false,
}: {
  templates: SchedulerTemplates,
  currentAgentId?: string,
  isAdmin?: boolean,
  isHostManager: boolean,
  hasImpersonationPermission?: boolean,
}): EditorState => ({
  templates: keyBy(templates, 'id'),
  calendars: [],
  accounts: [],
  me: [],
  errors: {},
  currentAgentId,
  currentActorId: null,
  isAdmin,
  hasImpersonationPermission,
  isHostManager,
  loading: {
    accounts: true,
    templates: true,
    teamTemplates: true,
    scheduledEvents: true,
    pastEvents: true,
    me: true,
  },
  triggers: {
    fetchAccounts: false,
    fetchTemplates: false,
    fetchTeamTemplates: false,
    fetchPrimaryActor: false,
    fetchMe: false,
    save: false,
  },
  scheduled_events_api_params: {
    start_at: `${Math.floor(Date.now() / 1000)}`, // Current time in seconds as an integer
    end_at: `${Math.floor(getTime(addDays(new Date(), 365)) / 1000)}`, // 365 days from now as an integer
    page: '1',
    limit: '10',
    sort_order: 'asc',
    title: '',
    distribution_rules: '',
  },
  past_events_api_params: {
    start_at: `${Math.floor(getTime(subDays(new Date(), 365)) / 1000)}`, // 365 days ago as an integer
    end_at: `${Math.floor(Date.now() / 1000)}`, // Current time as an integer
    sort_order: 'desc',
    page: '1',
    limit: '10',
    title: '',
    distribution_rules: '',
  },
  scheduled_events: {total_pages: 0, data: []},
  past_events: {total_pages: 0, data: []},
  merge_scheduled_events: false,
  initial_load_upcoming: true,
  initial_load_past: true,
});

// selectors, mostly to avoid re-calling endpoints

export const selectCurrentAgentId = (state: EditorState): ?string =>
  state.currentAgentId;
export const selectCurrentActorId = (state: EditorState): ?string =>
  state.currentActorId;
export const selectHasImpersonationPermission = (state: EditorState): boolean =>
  state.hasImpersonationPermission;
export const selectIsAdmin = (state: EditorState): boolean => state.isAdmin;
export function selectCalendars(state: EditorState): SchedulerCalendars {
  return state.calendars;
}

export const selectIsImpersonating = (state: EditorState): boolean =>
  state.currentActorId !== null &&
  state.currentActorId !== state.currentAgentId;

export function selectAccounts(state: EditorState): SchedulerAccounts {
  return state.accounts;
}

export function selectBookingAccountsUseOffice360(
  state: EditorState,
  template: SchedulerTemplate,
): boolean {
  const isRoundRobin = [
    'round_robin_availability',
    'round_robin_fairness',
  ].includes(template.distribution_rule);

  const bookingAccountsProviders = template.booking_accounts
    .filter((bookingAccount) => isRoundRobin || bookingAccount.is_host)
    .map(
      ({account_id}) =>
        state.accounts.find((account) => account.id === account_id)?.provider ??
        '',
    );

  return !!intersection(
    ['outlook', 'eas', 'ews', 'microsoft'],
    bookingAccountsProviders,
  ).length;
}

export const selectSyncedAccounts: (state: EditorState) => SchedulerAccounts =
  createSelector(selectAccounts, (accts) =>
    accts.filter((acct) => acct.is_synced),
  );

export const selectSyncedAccountIds: (state: EditorState) => string[] =
  createSelector(selectSyncedAccounts, (accts) => accts.map((acct) => acct.id));

const selectAccountsForId = (accounts: SchedulerAccounts, id: ?string) =>
  id ? accounts.filter((acc) => acc.agent_id === id) : [];

// select the agent_settings for an account with id
export const selectAgentSettingsForAccountId = (
  state: EditorState,
  accountId: string,
): ?AgentSettings => {
  const accounts = selectSyncedAccounts(state);
  const acctForId = accounts.find((acc) => acc.id === accountId);
  if (acctForId) {
    return acctForId.agent_settings;
  }
  return null;
};

// select accounts in order of time created, newest accounts are first
export const selectMyAccounts: (state: EditorState) => SchedulerAccounts =
  createSelector(
    selectSyncedAccounts,
    // we always need to filter for synced accounts
    // as it's possible to connect an unsyncable acct
    // that will never work
    selectCurrentAgentId,
    selectAccountsForId,
  );

export const selectProxyAccounts: (state: EditorState) => SchedulerAccounts =
  createSelector(
    selectSyncedAccounts,
    // we always need to filter for synced accounts
    // as it's possible to connect an unsyncable acct
    // that will never work
    selectCurrentActorId,
    selectAccountsForId,
  );

export const selectPrimaryAccount: (state: EditorState) => ?SchedulerAccount =
  createSelector(selectMyAccounts, (accts) => accts[0] || null);

export const selectPrimaryActor: (state: EditorState) => ?SchedulerAccount =
  createSelector(selectProxyAccounts, (accts) => accts[0] || null);

/**
 * select either the current user (non-admin) or the current assumed actor
 *
 * use this selector when trying to perform actions as either an agent or user
 * you almost always want this instead of trying to combine the above selectors
 *
 * @param  {EditorState} state              the reducer state
 * @return {(SchedulerAccount|null)}        a possibly null scheduler account
 */
export const selectPrimaryAgent: (state: EditorState) => ?SchedulerAccount =
  createSelector(
    selectPrimaryAccount,
    selectPrimaryActor,
    (acct, actor) => actor ?? acct,
  );

/**
 * Select the id of the current user (non-admin) or the current assumed actor
 *
 * use this selector when constructing other selectors here that operate on
 * the current ui.
 * @param  {EditorState} state: EditorState   the reducer state
 * @return {string|null}        the id of the current user (actor or not)
 */
export const selectPrimaryAgentId: (state: EditorState) => ?string =
  createSelector(selectPrimaryAgent, (agent) => agent?.id);

const selectTemplates = (
  state: EditorState,
): {[id: string]: SchedulerTemplate} => state.templates;

const selectTemplatesAsArray = createSelector(selectTemplates, (templates) =>
  values(templates),
);

export const selectTemplatesForAgent = (
  templates: SchedulerTemplates,
  agentId: ?string,
): SchedulerTemplates =>
  agentId
    ? templates.filter(
        ({agent_id, is_team_template}) =>
          agentId === agent_id && !is_team_template,
      )
    : [];

export const selectMyTemplates: (state: EditorState) => SchedulerTemplates =
  createSelector(
    selectTemplatesAsArray,
    selectCurrentAgentId,
    selectTemplatesForAgent,
  );

// FIXME(marcos) - make this selectCurrentAgentTemplates
export const selectActorTemplates: (state: EditorState) => SchedulerTemplates =
  createSelector(
    selectTemplatesAsArray,
    selectCurrentActorId,
    selectTemplatesForAgent,
  );

/**
 * Return the templates for the current user or the current assumed actor.
 *
 * use this selector when displaying or finding a list of templates in the ui.
 *
 * selecting all templates will return all templates an admin has seen and
 * using other selectors may show the wrong user's templates (Either admin or not)
 * @param  {[type]} state: EditorState   [description]
 * @return {SchedulerTemplates}        [description]
 */
export const selectPrimaryAgentTemplates: (
  state: EditorState,
) => SchedulerTemplates = createSelector(
  selectTemplatesAsArray,
  selectPrimaryAgentId,
  selectTemplatesForAgent,
);

/**
 * Return all team templates
 *
 * @param  {[type]} state: EditorState   [description]
 * @return {[type]}        [description]
 */
export const selectTeamTemplates: (state: EditorState) => SchedulerTemplates =
  createSelector(selectTemplatesAsArray, (templates) =>
    templates.filter(({is_team_template}) => is_team_template),
  );

/**
 * convert an array of booking_slots to grouped booking rows
 * @param  {BookingSlot[]} booking_slots returned from the api
 * @return {BookingRow[]}        BookingRow[]
 */
export function bookingSlotsAsBookingRows(slots: BookingSlot[]): BookingRow[] {
  const groupedAvailability = groupBy(slots, (slot) => slot.rule_id);

  const initialTimes: BookingRow[] = map(
    groupedAvailability,
    (value, rule_id) => ({
      days: value.map((v: BookingSlot) => v.day_of_week),
      start_time: value[0].start_time,
      end_time: value[0].end_time,
      rule_id,
    }),
  );

  return initialTimes;
}

/**
 * the inverse of bookingSlotsAsBookingRows used to persist an edited value back
 * to the reducer/template state
 * @param  { BookingRow[] } rows  a set of booking rows
 * @return { BookingSlot[] }      a value suitable for filling into open_booking_slots
 */
export function bookingRowsAsBookingSlots(rows: BookingRow[]): BookingSlot[] {
  return rows.flatMap((timeDef) =>
    timeDef.days.map((day) => ({
      day_of_week: day,
      start_time: timeDef.start_time,
      end_time: timeDef.end_time,
      rule_id: timeDef.rule_id,
    })),
  );
}

export function getEditableCalendarsForAccount(
  calendars: SchedulerCalendars,
  accountId: ?string,
): SchedulerCalendars {
  return calendars.filter(
    (calendar) => !calendar.read_only && accountId === calendar.account_id,
  );
}

/**
 * return all calendars that you can currently edit, either as an admin or as yourself
 * @param  {[type]}   state: EditorState   [description]
 * @return {SchedulerCalendars}          [description]
 */
export const selectEditableCalendars: (
  state: EditorState,
) => SchedulerCalendars = createSelector(
  selectCalendars,
  selectPrimaryAgentId,
  getEditableCalendarsForAccount,
);

/**
 * determine ConnectionState by looking at scheduler accounts and determining
 * if any are synced. users that have disconnected their accounts will show an
 * account that is !is_synced and users that have never connected will show
 * no accounts.
 * @param  {[type]} ConnectionState [description]
 * @return {ConnectionState}                 [description]
 */
export const selectConnectionStateFromAccounts = (
  myAccounts: ?SchedulerAccounts,
): ConnectionState =>
  myAccounts && myAccounts.length > 0
    ? myAccounts.some((acct: SchedulerAccount) => acct.is_synced)
      ? 'connected'
      : 'disconnected'
    : 'never-connected';

/**
 * returns the connectionstate for the current user (has no effect for admin), always
 * reflects the current sense user's calendar connection status
 * @param  {[type]} ConnectionState [description]
 * @return {[type]}                 [description]
 */
export const selectConnectionState: (state: EditorState) => ConnectionState =
  createSelector(selectMyAccounts, selectConnectionStateFromAccounts);

/**
 * a boolean to determine if you are currently inhabiting another user's account.
 * @param  {[type]} state: EditorState   [description]
 * @return {[type]}        [description]
 */
export const selectIsEditingAsAdmin: (state: EditorState) => boolean =
  createSelector(
    selectIsAdmin,
    selectCurrentAgentId,
    selectCurrentActorId,
    (isAdmin, agentId, actorId) => {
      if (isAdmin && actorId != null && agentId !== actorId) {
        return true;
      }
      return false;
    },
  );

export const selectIsEditingAsImpersonator: (state: EditorState) => boolean =
  createSelector(
    selectIsAdmin,
    selectHasImpersonationPermission,
    selectCurrentActorId,
    (isAdmin, hasImpersonationPermission, actorId) => {
      if (!isAdmin && hasImpersonationPermission && actorId != null) {
        return true;
      }
      return false;
    },
  );

export const selectMe = (state: EditorState): SchedulerAccounts => {
  return state.me;
};

export const selectConnectionStateFromMe: (
  state: EditorState,
) => ConnectionState = createSelector(
  selectMe,
  selectConnectionStateFromAccounts,
);

export const selectIsNotConnectedFromMe: (state: EditorState) => boolean =
  createSelector(selectConnectionStateFromMe, (connectionState) => {
    return connectionState !== 'connected';
  });

export const selectIsNotConnected: (state: EditorState) => boolean =
  createSelector(
    selectConnectionState,
    selectPrimaryActor,
    ({loading}: EditorState) => loading,
    (connectionState, primaryActor, loadingState) =>
      connectionState !== 'connected' &&
      loadingState.accounts === false &&
      // we used to check that accounts.length > 0, but
      // it's better to check that we've loaded accounts
      primaryActor == null,
  );

export const selectAgentName = (agent: ?SchedulerAccount): string => {
  if (!agent) {
    return '';
  }
  if (agent.agent_name === '') {
    return agent.agent_email_address;
  }
  return agent.agent_name;
};

export const selectPrimaryActorName: (state: EditorState) => string =
  createSelector(selectPrimaryActor, selectAgentName);

type ActorApiParams = {actor: string, agent_id?: string};
export const selectApiParametersForCurrentAgent: (
  state: EditorState,
) => ActorApiParams = createSelector(
  selectIsEditingAsAdmin,
  selectIsEditingAsImpersonator,
  selectCurrentActorId,
  (isAdmin, isImpersonator, actorId) => {
    if (isAdmin && actorId != null) {
      return {actor: 'admin', agent_id: actorId};
    } else if (!isAdmin && isImpersonator && actorId != null) {
      return {actor: 'impersonator', agent_id: actorId};
    } else {
      return {actor: 'self'};
    }
  },
);

export const sortReminderComparator = (a: Reminder, b: Reminder): number =>
  getDurationInSeconds(b.time_before) - getDurationInSeconds(a.time_before);

function getDurationInSeconds(duration: Duration): number {
  switch (duration.unit) {
    case 'minutes':
      return duration.amount * 60;
    case 'hours':
      return duration.amount * 60 * 60;
    case 'days':
      return duration.amount * 60 * 60 * 24;
    default:
      throw new Error(`Invalid duration unit ${duration.unit}`);
  }
}
